import React, { createContext, useContext, useState } from "react";
import { Auth } from "aws-amplify";
import { AwsConfigAuth } from "../utils/config/auth";
import openNotificationWithIcon from "../utils/hooks/openNotificationWithIcon";
import { useTranslation } from "react-i18next";

// Configuração do Amplify
Auth.configure(AwsConfigAuth);

// Criação do Contexto
const AuthContext = createContext(null);

// Hook personalizado
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext deve ser usado dentro de um AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const { t } = useTranslation();
    const [user, setUser] = useState(null);
    const [userSignIn, setUserSignIn] = useState(null);
    const [isLogging, setIsLogging] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
    const [sendedEmailToRecoverPassword, setSendedEmailToRecoverPassword] = useState(false);
    const [smsMfa, setSmsMfa] = useState(false);

    // Função para buscar o usuário autenticado
    const fetchUser = async (userSI) => {
        try {
            const userData = await Auth.currentAuthenticatedUser();
            localStorage.setItem(`adeporto_token`, userData?.signInUserSession?.accessToken?.jwtToken);
            const tags = userData?.attributes?.['custom:tags'];
            localStorage.setItem(`adeporto_tags`, tags);
            const zones = userData?.attributes?.['custom:zones'];
            localStorage.setItem(`adeporto_zones`, zones);
            const tenant = userData?.attributes?.['custom:tenant'];
            localStorage.setItem(`adeporto_tenant`, tenant);
            
            setUser(userData);
            setIsAuthenticating(false);
            setIsError(false);
            setSmsMfa(false);
            // logger.info('info', 'User loged in');
        } catch (error) {
            if(error === 'The user is not authenticated' && userSI?.challengeName === "NEW_PASSWORD_REQUIRED"){
                setIsAuthenticating(true);
                openNotificationWithIcon('warning', t('warning'), t('firstLogin'));
            } else if(error === 'The user is not authenticated' && (userSI?.challengeName === "SMS_MFA" || userSI?.challengeName === 'SOFTWARE_TOKEN_MFA')){
                setSmsMfa(true);
                openNotificationWithIcon('success', t('success'), t('codeSentByMessage'));
            }
            setIsError(true);
        }
    };

    // Funções de login e logout
    const login = async (user) => {
        try {
            setIsLogging(true);
            const userSI = await Auth.signIn(user.username, user.password);
            setUserSignIn(userSI);//To check in case the user is not authenticated;
            fetchUser(userSI);
            // logger.info('User loged in');
        } catch (error) {
            // logger.error('Error in user login');
            console.error('Erro no login', error);
            setIsError(true);
            openNotificationWithIcon('error', t('error'), t('incorrectFields'));
        } finally {
            setIsLogging(false);
        }
    };

    const logout = async () => {
        try {
            await clearLocalStorage();
            await Auth.signOut();
            setUser(null);
            // logger.info('User loged out');
        } catch (error) {
            // logger.error('Error in user logout')
            console.error('Erro no logout', error);
        }
    };

    const authenticateUser = async (user) => {
        try {
            if (user?.newPassword === user?.repeatNewPassword) {
                await Auth.completeNewPassword(userSignIn, user.newPassword);
                console.log('Password changed successfully');
                fetchUser();
            } else {
                console.log('error not the same password');
                openNotificationWithIcon('error', t('error'), t('passwordNoMatch'));
            }
        } catch (error) {
            // logger.error('Error changing password');
            console.error('Error changing password', error);
            openNotificationWithIcon('error', t('error'), t('passwordNotValid'));
        }
    }

    const sendEmailToRecoverPassword = async (user) => {
        await Auth.forgotPassword(user?.username);
        openNotificationWithIcon('success', t('success'),t('emailSentCode'));
        console.log('Código de verificação enviado');
        // A função retorna sucesso se o e-mail for enviado
        setSendedEmailToRecoverPassword(true);
    }

    const recoverPassword = async (user) => {
        try {
            if (user?.newPassword === user?.repeatNewPassword) {
                await Auth.forgotPasswordSubmit(user.username, user.passwordCode, user.newPassword);
                console.log('Senha redefinida com sucesso');
                // Redirecionar o usuário para a tela de login ou fazer o login automaticamente
                setIsRecoveringPassword(false);
                setSendedEmailToRecoverPassword(false);
            } else {
                console.log('error not the same passowrd');
                openNotificationWithIcon('error', t('error'), t('passwordNoMatch'));
            }
        } catch (error) {
            // logger.error('Error reseting password')
            console.error('Erro ao redefinir senha', error);
            openNotificationWithIcon('error', t('error'), t('errorResetPassword'));
            // if(error.__type = 'CodeMismatchException'){
            //     openNotificationWithIcon('error', t('error'), 'code');
            // }else if(error['__type'] = 'InvalidPasswordException'){
            //     openNotificationWithIcon('error', t('error'), 'require,mnets password');
            // } else{
            //     openNotificationWithIcon('error', t('error'), 'Something went wrong!!!!!');
            // }
            
        }
    }

    const loginWithMfa = async (user) => {
        try {
            await Auth.confirmSignIn(
                userSignIn,   // O objeto 'user' obtido do Auth.signIn
                user.mfaCode,   // Código MFA fornecido pelo usuário
                "SMS_MFA" // Desafio MFA (SMS_MFA ou SOFTWARE_TOKEN_MFA)
            );
            fetchUser();
            // O usuário está autenticado
        } catch (err) {
            console.log(err);
        }
    }

    const checkToken = async () => {
        const expireDate = user?.signInUserSession?.accessToken?.payload?.exp;
        const actualDate = new Date().getTime() / 1000;
        if(!expireDate || (actualDate > expireDate)){
            await fetchUser();
        }
    }

    const clearLocalStorage = () => {
        return new Promise((resolve, reject) => {
            try {
                localStorage.removeItem(`adeporto_tenant`);
                localStorage.removeItem(`adeporto_token`);
                localStorage.removeItem(`adeporto_tags`);
                localStorage.removeItem(`adeporto_zones`);
                resolve();
            } catch (error) {
                reject(error);
            }
        });
    };

    const value = {
        user,
        // userTenant,
        login,
        logout,
        isLogging,
        isError,
        isAuthenticating,//Authenticate user
        authenticateUser,
        userSignIn,
        smsMfa,//Login with MFA
        loginWithMfa,
        isRecoveringPassword,//Recover password
        sendedEmailToRecoverPassword,
        sendEmailToRecoverPassword,
        setIsRecoveringPassword,
        recoverPassword,
        checkToken//Validate token
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};